import type React from "react";

import { AuthnForm } from "@/web/auth/AuthnForm";
import { useAuthnDialogFlow } from "@/web/auth/useAuthnDialogFlow";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/web/components/primitives/dialog";

type AuthnFormDialogProps = {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactElement;
  showCloseButton?: boolean;
  title?: string;
  description?: string;
  /**
   * Pre-fills the email field and skips the method picker. Display only — the
   * user still authenticates normally. See `UseAuthnDialogFlowOptions`.
   */
  defaultEmail?: string | undefined;
  /** Where to land after signing in; see `UseAuthnDialogFlowOptions`. */
  redirectTo?: string | undefined;
};

export function AuthnFormDialog({
  open,
  defaultOpen,
  onOpenChange,
  trigger,
  showCloseButton = true,
  title = "Continue",
  description = "Use Google or a magic link to access this page.",
  defaultEmail,
  redirectTo,
}: AuthnFormDialogProps) {
  const flow = useAuthnDialogFlow({ defaultEmail, redirectTo });

  return (
    <Dialog open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent className="max-w-105 px-6 py-16" showCloseButton={showCloseButton}>
        <DialogHeader className="mb-2 gap-3 text-left">
          <DialogTitle className="withus-strong text-center text-2xl">{title}</DialogTitle>
          <DialogDescription className="text-center text-sm text-muted-foreground">
            {description}
          </DialogDescription>
        </DialogHeader>
        <AuthnForm flow={flow} />
      </DialogContent>
    </Dialog>
  );
}
